import React, {useEffect, useState, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {fetchReports, fetchVerificationStates, exportReport} from '../services/reportsApi';
import ReportTable from '../components/ReportTable';

// Helper: fetch Keycloak token using client credentials (optional; requires env vars)
async function getKeycloakToken(tokenUrl: string, clientId: string, clientSecret: string) {
    const body = new URLSearchParams();
    body.append('client_id', clientId);
    body.append('client_secret', clientSecret);
    body.append('grant_type', 'client_credentials');

    const res = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
    });
    if (!res.ok) throw new Error(`Keycloak token request failed: ${res.status}`);
    const data = await res.json();
    return data.access_token as string;
}

async function fetchKeycloakUsers(usersUrl: string, accessToken: string, maxUsers = '2000') {
    const url = `${usersUrl}?max=${encodeURIComponent(maxUsers)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) throw new Error(`Keycloak users request failed: ${res.status}`);
    return await res.json();
}

function extractCellphoneFromUser(u: any) {
    if (!u) return '';
    if (u.attributes && u.attributes.cellphone) {
        const attr = u.attributes.cellphone;
        if (Array.isArray(attr)) return attr[0] || '';
        if (typeof attr === 'string') return attr || '';
    }
    if (u.cellphone) return u.cellphone || '';
    return '';
}

function buildUserMap(users: any[]): Map<string, { firstName: string; lastName: string; cellphone: string }> {
    const map = new Map<string, { firstName: string; lastName: string; cellphone: string }>();
    users.forEach(u => {
        if (u && u.email) {
            map.set(String(u.email).toLowerCase(), {
                firstName: u.firstName || '',
                lastName: u.lastName || '',
                cellphone: extractCellphoneFromUser(u) || '',
            });
        }
    });
    return map;
}

async function augmentReportsWithAgents(rows: any[]): Promise<any[]> {
    const tokenUrl = process.env.REACT_APP_KEYCLOAK_TOKEN_URL;
    const usersUrl = process.env.REACT_APP_KEYCLOAK_USERS_URL;
    const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_KEYCLOAK_CLIENT_SECRET;
    const maxUsers = process.env.REACT_APP_KEYCLOAK_MAX_USERS || '2000';

    // Prepare empty augmentation
    const emptyAug = (r: any) => Object.assign({}, r, { agentFirstName: '', agentLastName: '', agentCellphone: '' });

    if (!tokenUrl || !usersUrl || !clientId || !clientSecret) {
        // env not configured: backend may already have agent fields — do not overwrite, just return rows as-is
        return rows;
    }

    try {
        const token = await getKeycloakToken(tokenUrl, clientId, clientSecret);
        const users = await fetchKeycloakUsers(usersUrl, token, maxUsers);
        const userMap = buildUserMap(users || []);

        return rows.map(r => {
            const capturedBy = r.capturedBy ? String(r.capturedBy).toLowerCase() : null;
            const modifiedBy = r.modifiedBy ? String(r.modifiedBy).toLowerCase() : null;
            const matchEmail = capturedBy && userMap.has(capturedBy) ? capturedBy : (modifiedBy && userMap.has(modifiedBy) ? modifiedBy : null);
            const agent = matchEmail ? userMap.get(matchEmail)! : { firstName: '', lastName: '', cellphone: '' };
            // preserve any agent fields already present (from backend) and only fill missing values
            return Object.assign({}, r, {
                agentFirstName: r.agentFirstName || agent.firstName,
                agentLastName: r.agentLastName || agent.lastName,
                agentCellphone: r.agentCellphone || agent.cellphone,
            });
        });
    } catch (err) {
        // On any failure, return rows with empty agent fields to avoid breaking UI
        console.error('Failed to augment reports with agents:', err);
        return rows.map(emptyAug);
    }
}

const AdminReports: React.FC = () => {
    const navigate = useNavigate();
    const [reports, setReports] = useState<any[]>([]);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [userId, setUserId] = useState('');
    const [msisdn, setMsisdn] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('');
    const [verificationStates, setVerificationStates] = useState<string[]>([]);
    // Snapshot of filters currently applied to results/pagination
    const [currentFilters, setCurrentFilters] = useState({ start: '', end: '', userId: '', msisdn: '', verificationStatus: '' });
    const filtersRef = useRef(currentFilters);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastFetchInfo, setLastFetchInfo] = useState<{filters: any; time: string | null}>({filters: null, time: null});
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);

    const API_BASE = process.env.REACT_APP_API_SELFCARE_URL as string;

    useEffect(() => {
        fetchVerificationStates().then(setVerificationStates).catch(_ => setVerificationStates([]));
    }, []);

    const fetchAndSetReports = async (p: number, filters?: { start?: string; end?: string; userId?: string; msisdn?: string; verificationStatus?: string }) => {
        const usedFilters = filters || filtersRef.current || currentFilters;
        setLoading(true);
        setError('');
        setLastFetchInfo({filters: usedFilters, time: new Date().toISOString()});
            try {
            const data = await fetchReports(
                usedFilters.start,
                usedFilters.end,
                usedFilters.userId,
                usedFilters.msisdn,
                usedFilters.verificationStatus,
                p,
                pageSize
            );
            const rows = data.rows || [];
            // Augment with agent fields (this will no-op to empty fields if env not configured)
            const augmented = await augmentReportsWithAgents(rows);
            setReports(augmented);
            setTotalRecords(data.totalRecords || 0);
        } catch (e) {
            setReports([]);
            setTotalRecords(0);
            setError((e as any)?.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    // Fetch when the page changes (pagination) — use filtersRef so pagination uses the correct filters
    useEffect(() => {
        fetchAndSetReports(page, filtersRef.current);
    }, [page]);

    // Initial load - run a search to ensure filtersRef and currentFilters are set
    useEffect(() => {
        // call handleSearch to capture current (empty) filters and fetch
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearFilters = () => {
        const empty = { start: '', end: '', userId: '', msisdn: '', verificationStatus: '' };
        setStart(''); setEnd(''); setUserId(''); setMsisdn(''); setVerificationStatus('');
        setCurrentFilters(empty);
        filtersRef.current = empty;
        setPage(1);
    };

    const handleSearch = async () => {
        // Capture current input values as the active filters for this search
        const filters = { start, end, userId, msisdn, verificationStatus };
        setCurrentFilters(filters);
        filtersRef.current = filters; // keep a synchronous ref so pagination effect reads correct filters
        setPage(1);
        await fetchAndSetReports(1, filters);
    };

    // Debounced live search: when any filter input changes, automatically search
    React.useEffect(() => {
        const timer = setTimeout(async () => {
            const filters = { start, end, userId, msisdn, verificationStatus };
            setCurrentFilters(filters);
            filtersRef.current = filters;
            if (page !== 1) {
                setPage(1);
            } else {
                await fetchAndSetReports(1, filters);
            }
        }, 350); // 350ms debounce

        return () => clearTimeout(timer);
    }, [start, end, userId, msisdn, verificationStatus]);

    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

    const handleExportClick = async (type: string) => {
        const filters = {
            startDate: currentFilters.start,
            endDate: currentFilters.end,
            userId: currentFilters.userId,
            msisdn: currentFilters.msisdn,
            verificationStatus: currentFilters.verificationStatus
        };
        try {
            const url = await exportReport(type, filters);
            window.open(url, '_blank');
        } catch (e) {
            console.error('Export error', e);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            <div className="max-w-6xl mx-auto bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-green-800 mb-6">KYC Agent Reports</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <input value={start} onChange={e => setStart(e.target.value)} type="date" className="p-2 border rounded" />
                    <input value={end} onChange={e => setEnd(e.target.value)} type="date" className="p-2 border rounded" />
                    <input value={userId} onChange={e => setUserId(e.target.value)} placeholder="User ID" className="p-2 border rounded" />
                </div>
                {/* Debug / status */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700">{error}</div>
                )}
                {/* lastFetchInfo display removed */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <input value={msisdn} onChange={e => setMsisdn(e.target.value)} placeholder="MSISDN" className="p-2 border rounded" />
                    <select value={verificationStatus} onChange={e => setVerificationStatus(e.target.value)} className="p-2 border rounded">
                        <option value="">All</option>
                        {verificationStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="flex gap-2">
                        <button type="button" onClick={handleSearch} className="bg-green-700 hover:bg-green-700 text-white px-4 py-2 rounded">Search</button>
                        <button type="button" onClick={clearFilters} className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded">Clear</button>
                    </div>
                </div>

                <div className="flex gap-2 mb-4">
                        <button type="button" onClick={() => handleExportClick('csv')} className="bg-green-700 hover:bg-green-500 text-white px-4 py-2 rounded">Export CSV</button>
                    <button type="button" onClick={() => handleExportClick('excel')} className="bg-green-700 hover:bg-green-500 text-white px-4 py-2 rounded">Export Excel</button>
                </div>

                {loading ? (
                    <div className="text-center py-8">
                        <svg className="animate-spin mx-auto h-8 w-8 text-green-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-sm text-gray-600 mt-2">Fetching KYC report...</p>
                    </div>
                ) : (
                    <ReportTable reports={reports} />
                )}

                <div className="flex items-center justify-between mt-4">
                    <div>Showing {reports.length} of {totalRecords}</div>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setPage(p => Math.max(1, p-1))} className="px-3 py-1 bg-gray-100 rounded" disabled={page<=1}>Prev</button>
                        <span>Page {page} of {totalPages}</span>
                        <button type="button" onClick={() => setPage(p => Math.min(totalPages, p+1))} className="px-3 py-1 bg-gray-100 rounded" disabled={page>=totalPages}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminReports;



